import dayjs from "dayjs";
import { RRule } from "rrule";
import {
  IBlockedDateEmployees,
  IBlockedDateEvent,
  IBlockedDatesResponse,
  IGroupedBlockedDateEvents,
  IOdpBlockDate,
} from "../@types/blockedDates";

type monthMinMaxDates = {
  minDate: Date;
  maxDate: Date;
};

interface recurrenceMinMaxDates {
  [index: string]: monthMinMaxDates;
}

export const getEmployeeIdsFromBlockedDate = (
  employees: IBlockedDateEmployees[]
): number[] => employees.map((employee) => employee.employee_id);

export const getBlockedDateEvent = (
  blockedDate: IOdpBlockDate,
  employees: IBlockedDateEmployees[] | null = null
): IBlockedDateEvent[] => {
  const EVENTS = [];
  const blockedDateInstance = blockedDate;

  if (blockedDate.rrule.indexOf("BYDAY=") > 0) {
    /*
        UPDATE STRING to accomodate https://www.npmjs.com/package/rrule/v/2.7.2#differences-from-icalendar-rfc
        RRule has no byday keyword. The equivalent keyword has been replaced by the byweekday keyword, 
        to remove the ambiguity present in the original keyword.
        */
    blockedDateInstance.rrule = blockedDate.rrule.replace(
      "BYDAY=",
      "BYWEEKDAY="
    );
  }

  const rrule = RRule.fromString(blockedDateInstance.rrule);
  const frequency = rrule.options.freq;
  const allRecurrenceInstances = rrule.all();

  if (frequency === RRule.DAILY) {
    const firstDayInstance = allRecurrenceInstances[0];
    const lastDayInstance =
      allRecurrenceInstances[allRecurrenceInstances.length - 1];

    const event = {
      id: blockedDateInstance.id,
      title: blockedDateInstance.title,
      start: dayjs(firstDayInstance).startOf("day").toDate(),
      end: dayjs(lastDayInstance).endOf("day").toDate(),
      allDay: true,
      rrule: blockedDateInstance.rrule,
      block_reason: blockedDateInstance.block_reason,
      created_by: blockedDateInstance.created_by,
      created_by_name: blockedDateInstance.created_by_name,
      created_at: blockedDateInstance.created_at,
      updated_by: blockedDateInstance.updated_by,
      updated_by_name: blockedDateInstance.updated_by_name,
      updated_at: blockedDateInstance.updated_at,
      employees,
    };
    EVENTS.push(event);
  } else if (frequency === RRule.YEARLY) {
    if (rrule.options.byyearday) {
      const byYearDaysLength = rrule.options.byyearday.length;

      for (
        let i = 0;
        i < allRecurrenceInstances.length;
        i += byYearDaysLength
      ) {
        // if leapYear pad by a day

        let startDate = allRecurrenceInstances.slice(
          i,
          i + byYearDaysLength
        )[0];
        let endDate = allRecurrenceInstances.slice(i, i + byYearDaysLength)[
          byYearDaysLength - 1
        ];
        const nthDayOfYear = dayjs(endDate).dayOfYear();

        const minNthDay = Math.min(...rrule.options.byyearday);
        const maxNthDay = Math.max(...rrule.options.byyearday);

        if (dayjs(startDate).isLeapYear() && minNthDay > 60) {
          startDate = dayjs(startDate).add(1, "day").toDate();
          endDate = dayjs(endDate).add(1, "day").toDate();
        } else if (
          dayjs(startDate).isLeapYear() &&
          nthDayOfYear === maxNthDay
        ) {
          startDate = dayjs(startDate).toDate();
          endDate = dayjs(endDate).add(1, "day").toDate();
        }

        const event = {
          id: blockedDateInstance.id,
          title: blockedDateInstance.title,
          start: dayjs(startDate).startOf("day").toDate(),
          end: dayjs(endDate).endOf("day").toDate(),
          allDay: true,
          rrule: blockedDateInstance.rrule,
          block_reason: blockedDateInstance.block_reason,
          created_by: blockedDateInstance.created_by,
          created_by_name: blockedDateInstance.created_by_name,
          created_at: blockedDateInstance.created_at,
          updated_by: blockedDateInstance.updated_by,
          updated_by_name: blockedDateInstance.updated_by_name,
          updated_at: blockedDateInstance.updated_at,
          employees,
        };
        EVENTS.push(event);
      }
    } else {
      // only appears on 29th of feb
      allRecurrenceInstances.forEach((yearlyInstance: any) => {
        const event = {
          id: blockedDateInstance.id,
          title: blockedDateInstance.title,
          start: dayjs(yearlyInstance).startOf("day").toDate(),
          end: dayjs(yearlyInstance).endOf("day").toDate(),
          allDay: true,
          rrule: blockedDateInstance.rrule,
          block_reason: blockedDateInstance.block_reason,
          created_by: blockedDateInstance.created_by,
          created_at: blockedDateInstance.created_at,
          created_by_name: blockedDateInstance.created_by_name,
          updated_by: blockedDateInstance.updated_by,
          updated_by_name: blockedDateInstance.updated_by_name,
          updated_at: blockedDateInstance.updated_at,
          employees,
        };
        EVENTS.push(event);
      });
    }
  } else if (frequency === RRule.MONTHLY) {
    const monthlyMinMaxPerYear: recurrenceMinMaxDates = {};

    for (let i = 0; i < allRecurrenceInstances.length; i += 1) {
      const index = `${allRecurrenceInstances[
        i
      ].getFullYear()}${allRecurrenceInstances[i].getMonth()}`;

      if (!monthlyMinMaxPerYear[index]) {
        monthlyMinMaxPerYear[index] = {
          minDate: allRecurrenceInstances[0],
          maxDate: new Date("1800"), // Intentional, maxDate will be updated below
        };
      }

      if (!monthlyMinMaxPerYear[index].maxDate) {
        monthlyMinMaxPerYear[index].maxDate = allRecurrenceInstances[i];
      } else {
        const maxDate =
          allRecurrenceInstances[i] > monthlyMinMaxPerYear[index].maxDate
            ? allRecurrenceInstances[i]
            : monthlyMinMaxPerYear[index].maxDate;
        monthlyMinMaxPerYear[index].maxDate = maxDate;
      }
    }
    Object.entries(monthlyMinMaxPerYear).forEach((keyValues) => {
      if (keyValues) {
        const event = {
          id: blockedDateInstance.id,
          title: blockedDateInstance.title,
          start: dayjs(keyValues[1].minDate).startOf("day").toDate(),
          end: dayjs(keyValues[1].maxDate).endOf("day").toDate(),
          allDay: true,
          rrule: blockedDateInstance.rrule,
          block_reason: blockedDateInstance.block_reason,
          created_by: blockedDateInstance.created_by,
          created_by_name: blockedDateInstance.created_by_name,
          created_at: blockedDateInstance.created_at,
          updated_by: blockedDateInstance.updated_by,
          updated_by_name: blockedDateInstance.updated_by_name,
          updated_at: blockedDateInstance.updated_at,
          employees,
        };
        EVENTS.push(event);
      }
    });
  } else if (frequency === RRule.WEEKLY) {
    allRecurrenceInstances.forEach((weeklyInstance: any) => {
      const event = {
        id: blockedDateInstance.id,
        title: blockedDateInstance.title,
        start: dayjs(weeklyInstance).startOf("day").toDate(),
        end: dayjs(weeklyInstance).endOf("day").toDate(),
        allDay: true,
        rrule: blockedDateInstance.rrule,
        block_reason: blockedDateInstance.block_reason,
        created_by: blockedDateInstance.created_by,
        created_at: blockedDateInstance.created_at,
        created_by_name: blockedDateInstance.created_by_name,
        updated_by: blockedDateInstance.updated_by,
        updated_by_name: blockedDateInstance.updated_by_name,
        updated_at: blockedDateInstance.updated_at,
        employees,
      };
      EVENTS.push(event);
    });
  }

  return EVENTS;
};

export const getAllBlockedDateEvents = (
  odpLimitsBlockedDates: IBlockedDatesResponse
): IBlockedDateEvent[] => {
  const employeeEvents: IBlockedDateEvent[] = [];
  const employerEvents: IBlockedDateEvent[] = [];

  const { employer: employerBlockedDates, employees: employeeBlockedDates } =
    odpLimitsBlockedDates;

  if (employerBlockedDates) {
    Object.keys(employerBlockedDates).forEach((entry) => {
      employerEvents.push(...getBlockedDateEvent(employerBlockedDates[entry]));
    });
  }

  if (employeeBlockedDates) {
    Object.keys(employeeBlockedDates).forEach((entry) => {
      const employees: IBlockedDateEmployees[] = [];
      let employeeBlockedDate: IOdpBlockDate = {
        id: "",
        title: "",
        rrule: "",
        block_reason: "",
        created_by: null,
        created_by_name: null,
        created_at: null,
        updated_by: null,
        updated_by_name: null,
        updated_at: null,
      };

      employeeBlockedDates[entry].forEach((blockedDate: any) => {
        employeeBlockedDate = {
          id: blockedDate.id,
          title: blockedDate.title,
          rrule: blockedDate.rrule,
          block_reason: blockedDate.block_reason,
          created_by: blockedDate.created_by,
          created_by_name: blockedDate.created_by_name,
          created_at: blockedDate.created_at,
          updated_by: blockedDate.updated_by,
          updated_by_name: blockedDate.updated_by_name,
          updated_at: blockedDate.updated_at,
        };
        employees.push({
          employee_id: blockedDate.employee_id,
          employee_name: blockedDate.employee_name,
          employee_id_number: blockedDate.employee_id_number,
          employee_number: blockedDate.employee_number,
          mobile_number: blockedDate.mobile_number,
          employee_branch: blockedDate.employee_branch,
        });
      });

      employeeEvents.push(
        ...getBlockedDateEvent(employeeBlockedDate, employees)
      );
    });
  }
  return Array.from(
    new Map(
      [...employerEvents, ...employeeEvents].map((event) => [event.id, event])
    ).values()
  );
};

const categorizeEvent = (
  event: IBlockedDateEvent,
  startOfToday: dayjs.Dayjs,
  endOfToday: dayjs.Dayjs
): "past" | "current" | "future" => {
  const eventStart = dayjs(event.start);
  const eventEnd = dayjs(event.end);

  // Helper function for non-recurring events
  const categorizeNonRecurring = (): "past" | "current" | "future" => {
    if (eventEnd.isBefore(startOfToday)) return "past";
    if (
      eventStart.isSame(startOfToday, "day") ||
      eventEnd.isSame(endOfToday, "day") ||
      (eventStart.isBefore(startOfToday) && eventEnd.isAfter(endOfToday))
    )
      return "current";
    return "future";
  };

  // If no rrule, handle as non-recurring event
  if (!event.rrule) return categorizeNonRecurring();

  // Handle recurring events
  const rrule = RRule.fromString(event.rrule);
  const todayOccurrences = rrule.between(
    startOfToday.toDate(),
    endOfToday.toDate(),
    true
  );

  // If there are occurrences today, it's current
  if (todayOccurrences.length > 0) return "current";

  // Check future occurrences
  const nextOccurrence = rrule.after(startOfToday.toDate(), true);
  return nextOccurrence ? "future" : "past";
};

export const getSplitBlockedDates = (
  events: IBlockedDateEvent[]
): IGroupedBlockedDateEvents => {
  const pastEvents: IBlockedDateEvent[] = [];
  const currentEvents: IBlockedDateEvent[] = [];
  const futureEvents: IBlockedDateEvent[] = [];

  const now = dayjs();
  const startOfToday = now.startOf("day");
  const endOfToday = now.endOf("day");

  events.forEach((event) => {
    const category = categorizeEvent(event, startOfToday, endOfToday);
    switch (category) {
      case "past":
        pastEvents.push(event);
        break;
      case "current":
        currentEvents.push(event);
        break;
      case "future":
        futureEvents.push(event);
        break;
      default:
        break;
    }
  });

  return { pastEvents, currentEvents, futureEvents };
};

export const splitRecurringEvents = (
  events: IBlockedDateEvent[]
): IBlockedDateEvent[] => {
  const splitEvents: IBlockedDateEvent[] = [];

  events.forEach((event) => {
    if (event.rrule) {
      const rrule = RRule.fromString(event.rrule);
      const allRecurrenceInstances = rrule.all();

      let currentRangeStart: Date | null = null;
      let currentRangeEnd: Date | null = null;

      allRecurrenceInstances.forEach((instance, index) => {
        const instanceStart = dayjs(instance).startOf("day").toDate();
        const instanceEnd = dayjs(instance).endOf("day").toDate();

        if (currentRangeStart === null || currentRangeEnd === null) {
          // Initialize the first range
          currentRangeStart = instanceStart;
          currentRangeEnd = instanceEnd;
        } else if (
          dayjs(instanceStart).diff(dayjs(currentRangeEnd), "day") > 1
        ) {
          // If there is a gap between the current instance and the previous range, push the previous range and start a new one
          splitEvents.push({
            ...event,
            start: currentRangeStart,
            end: currentRangeEnd,
          });
          currentRangeStart = instanceStart;
          currentRangeEnd = instanceEnd;
        } else {
          // Extend the current range
          currentRangeEnd = instanceEnd;
        }

        // Push the final range
        if (index === allRecurrenceInstances.length - 1) {
          splitEvents.push({
            ...event,
            start: currentRangeStart,
            end: currentRangeEnd,
          });
        }
      });
    } else {
      splitEvents.push(event);
    }
  });

  return splitEvents;
};
